import React from "react";
import { User, Stethoscope, ClipboardList, Pill, FlaskConical, ShieldCheck, Cloud } from "lucide-react";
import Reveal from "./Reveal";
import Stagger from "./Stagger";
import PillField from "./PillField";
import { useLanguage } from "../i18n/LanguageContext";

const ROLE_ICONS = [User, Stethoscope, ClipboardList, Pill, FlaskConical, ShieldCheck];

export default function Unified() {
  const { t } = useLanguage();
  const u = t.unified;

  return (
    <section id="unified" data-testid="unified-section" className="relative overflow-hidden py-24 sm:py-32 border-t border-line">
      <PillField items={t.scale.items} />
      <div className="relative shell">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal className="kicker text-textsoft">{u.kicker}</Reveal>
            <Reveal as="h2" delay={1} className="mt-5 font-display text-[34px] sm:text-[44px] lg:text-[48px] font-semibold text-ink tracking-tight leading-[1.06]">
              {u.h2}
            </Reveal>
            <Reveal as="p" delay={2} className="mt-5 text-[17px] text-textsoft leading-[1.6]">
              {u.sub}
            </Reveal>
            <Reveal delay={3} className="mt-7 rounded-2xl border border-green/25 bg-green-soft/60 p-5">
              <div className="flex items-start gap-3">
                <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-white text-green shrink-0">
                  <Cloud size={19} strokeWidth={1.8} />
                </span>
                <p className="text-[14.5px] text-green-dark leading-[1.6]">{u.cloud}</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={1}>
              <div className="glow rounded-[24px] bg-white border border-line p-7 sm:p-9" style={{ boxShadow: "0 40px 100px -44px rgba(15,42,71,0.35)" }}>
                <div className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-textsoft">{u.same}</div>

                {/* roles — stagger in, with a traveling live highlight */}
                <Stagger className="mt-6 grid grid-cols-3 gap-3">
                  {u.roles.map((label, i) => {
                    const Icon = ROLE_ICONS[i] || User;
                    return (
                      <div key={label} className="h-full">
                        <div
                          className="pop live-chip h-full flex flex-col items-center gap-2 rounded-xl border border-line bg-paper/70 py-4 cursor-default hover:bg-white"
                          style={{ animationDelay: `${(-(i) * (7 / 6)).toFixed(2)}s` }}
                        >
                          <span className="inline-flex w-11 h-11 items-center justify-center rounded-full bg-white border border-line text-ink">
                            <Icon size={18} strokeWidth={1.7} />
                          </span>
                          <span className="text-[12.5px] font-semibold text-ink">{label}</span>
                        </div>
                      </div>
                    );
                  })}
                </Stagger>

                {/* converging connector */}
                <div className="relative h-8" aria-hidden="true">
                  <svg viewBox="0 0 300 40" preserveAspectRatio="none" className="w-full h-full">
                    <path d="M50,0 C50,26 150,14 150,40" fill="none" stroke="#DBE6EF" strokeWidth="1.4" strokeDasharray="3 4" />
                    <path d="M150,0 L150,40" fill="none" stroke="#DBE6EF" strokeWidth="1.4" strokeDasharray="3 4" />
                    <path d="M250,0 C250,26 150,14 150,40" fill="none" stroke="#DBE6EF" strokeWidth="1.4" strokeDasharray="3 4" />
                  </svg>
                </div>

                {/* cloud hub with continuous sheen */}
                <div className="sheen grad-border relative rounded-2xl px-5 py-4 text-white flex items-center justify-center gap-3 text-center" style={{ background: "linear-gradient(120deg, #163A5C 0%, #0F2A47 70%)" }}>
                  <Cloud size={18} strokeWidth={1.9} className="text-green shrink-0" />
                  <span className="text-[13.5px] font-semibold tracking-wide">{u.hub}</span>
                </div>

                {/* modules — stagger in (wrapper reveals, inner pill pops) */}
                <Stagger className="mt-6 flex flex-wrap justify-center gap-2">
                  {u.modules.map((m) => (
                    <span key={m} className="inline-flex">
                      <span className="pop inline-flex items-center rounded-full border border-line bg-paper px-3 py-1.5 text-[12px] font-medium text-textsoft hover:border-blue/40 hover:text-ink hover:bg-white">
                        {m}
                      </span>
                    </span>
                  ))}
                </Stagger>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
